import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Pill from '../../components/ui/Pill';
import Icon from '../../components/ui/Icon';
import Mascot from '../../components/ui/Mascot';
import Thumb from '../../components/ui/Thumb';
import ProgressBar from '../../components/ui/ProgressBar';
import {api, getToken, setToken} from '../../lib/api';
import {currentUser, badges, myProjects} from '../../data/mock';
import styles from './Profile.module.scss';

function isProjectPublic (project) {
    return project.isPublic === true || project.is_public === true || Number(project.is_public) === 1;
}

function projectDraftKey (project) {
    const title = String(project.title ?? '').trim().toLowerCase();
    const category = String(project.category ?? '').trim().toLowerCase();
    const sourceLessonId = project.sourceLessonId ?? project.source_lesson_id;
    if (title) return `work:${title}:${category}`;
    if (isProjectPublic(project)) return `public:${project.id}`;
    if (sourceLessonId) return `lesson:${sourceLessonId}`;
    return `draft:${project.id}`;
}

function dedupeDrafts (projectRows) {
    const seen = new Set();
    return projectRows.filter(project => {
        const key = projectDraftKey(project);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

export default function Profile () {
    const navigate = useNavigate();
    const [user, setUser] = useState(currentUser);
    const [projects, setProjects] = useState(myProjects);
    const [badgeList, setBadgeList] = useState(badges);
    const [source, setSource] = useState('local');
    const [loggedIn, setLoggedIn] = useState(Boolean(getToken()));
    const [publishStatus, setPublishStatus] = useState('');

    useEffect(() => {
        api.listBadges().then(({badges: remoteBadges}) => {
            if (remoteBadges?.length) setBadgeList(remoteBadges);
        }).catch(() => {});
        if (!getToken()) return;
        let alive = true;
        Promise.all([api.me(), api.myProjects()]).then(([me, mine]) => {
            if (!alive) return;
            const backendUser = me.user;
            const uniqueProjects = dedupeDrafts(mine.projects);
            setUser({
                ...currentUser,
                name: backendUser.displayName,
                title: `Lv${backendUser.level} 探险家`,
                level: backendUser.level,
                xp: backendUser.xp,
                streak: backendUser.streak,
                projectsBuilt: uniqueProjects.length
            });
            setProjects(uniqueProjects.map(p => {
                const isPublic = isProjectPublic(p);
                return {
                    id: p.id,
                    title: p.title,
                    desc: p.description,
                    status: isPublic ? '已分享' : '草稿',
                    statusColor: isPublic ? 'green' : 'gray',
                    thumb: p.thumbnail || 'space',
                    isPublic,
                    sourceLessonId: p.source_lesson_id
                };
            }));
            setSource('backend');
        }).catch(() => setSource('local'));
        return () => {
            alive = false;
        };
    }, []);

    const xpPct = Math.round((user.xp / user.xpToNext) * 100);
    const logout = () => {
        setToken(null);
        setLoggedIn(false);
        navigate('/login');
    };

    const publishProject = async projectId => {
        if (!getToken()) {
            setPublishStatus('请先登录，再发布作品');
            return;
        }
        setPublishStatus('正在发布到作品广场…');
        try {
            await api.updateProject(projectId, {isPublic: true});
            setProjects(prev => prev.map(project => (
                project.id === projectId
                    ? {...project, isPublic: true, status: '已分享', statusColor: 'green'}
                    : project
            )));
            setPublishStatus('已发布到作品广场');
        } catch (err) {
            setPublishStatus(err.message || '发布失败，请稍后再试');
        }
    };
    const stats = [
        {icon: 'flame', color: 'orange', title: `连续学习 ${user.streak} 天`, sub: '继续保持！'},
        {icon: 'puzzle', color: 'blue', title: `完成 ${user.projectsBuilt} 个作品`, sub: '建造大师'},
        {icon: 'graduation', color: 'green', title: `掌握 ${user.skillsMastered} 项技能`, sub: '逻辑天才'}
    ];

    return (
        <div className={`cq-container ${styles.layout}`}>
            {/* 左列：头像 + 统计 */}
            <aside className={styles.sidebar}>
                <Card className={styles.userCard}>
                    <div className={styles.avatarWrap}>
                        <div className={styles.avatar}><Mascot size={110} /></div>
                        <button className={styles.editAvatar} aria-label="更换头像">
                            <Icon name="edit" size={16} />
                        </button>
                    </div>
                    <h1 className={styles.username}>{user.name}</h1>
                    <Pill color="blue" iconLeft={<Icon name="star" size={14} />}>
                        {source === 'backend' ? '后端账号已连接' : '演示账号'}
                    </Pill>
                    <div className={styles.levelBox}>
                        <div className={styles.levelRow}>
                            <span>等级 {user.level}</span>
                            <span className={styles.xpText}>
                                {user.xp.toLocaleString()} / {user.xpToNext.toLocaleString()} XP
                            </span>
                        </div>
                        <ProgressBar value={xpPct} color="green" label="经验值进度" />
                    </div>
                    {loggedIn && (
                        <button type="button" className={styles.logoutBtn} onClick={logout}>
                            退出登录
                        </button>
                    )}
                </Card>

                <Card className={styles.statsCard}>
                    <h2 className={styles.statsTitle}>成长记录</h2>
                    <ul className={styles.statList}>
                        {stats.map(s => (
                            <li key={s.title} className={styles.statItem}>
                                <span className={[styles.statIcon, styles[`ic_${s.color}`]].join(' ')}>
                                    <Icon name={s.icon} size={22} />
                                </span>
                                <div>
                                    <div className={styles.statHead}>{s.title}</div>
                                    <div className={styles.statSub}>{s.sub}</div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </Card>
            </aside>

            {/* 右列：徽章 + 作品 */}
            <div className={styles.main}>
                <Card className={styles.badgesCard}>
                    <div className={styles.sectionHead}>
                        <h2 className={styles.sectionTitle}>徽章收藏</h2>
                        <button className={styles.viewAll}>查看全部</button>
                    </div>
                    <div className={styles.badgeGrid}>
                        {badgeList.map(b => (
                            <div key={b.id} className={styles.badge}>
                                <span className={[styles.badgeIcon, styles[`bg_${b.color}`]].join(' ')}>
                                    <Icon name={b.earned ? b.icon : 'lock'} size={30} />
                                </span>
                                <span className={[styles.badgeName, b.earned ? '' : styles.badgeLocked].join(' ')}>
                                    {b.name}
                                </span>
                            </div>
                        ))}
                    </div>
                </Card>

                <div className={styles.sectionHead}>
                    <h2 className={styles.sectionTitle}>我的作品</h2>
                    <Button variant="primary" size="sm" iconLeft={<Icon name="plus" size={18} />} to="/lab/new">
                        新建作品
                    </Button>
                </div>
                {publishStatus && <div className={styles.publishStatus} role="status">{publishStatus}</div>}
                <div className={styles.projectGrid}>
                    {projects.map(p => (
                        <Card key={p.id} className={styles.projectCard} pad={false}>
                            <div className={styles.projectThumb}>
                                <Thumb theme={p.thumb} ratio="16 / 9" />
                                <span className={[styles.projectTag, styles[`tag_${p.statusColor}`]].join(' ')}>
                                    {p.status}
                                </span>
                            </div>
                            <div className={styles.projectBody}>
                                <h3 className={styles.projectTitle}>{p.title}</h3>
                                <p className={styles.projectDesc}>{p.desc}</p>
                                <div className={styles.projectActions}>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        block
                                        to={`/lab/${p.id}`}
                                        iconLeft={<Icon name="edit" size={16} />}
                                    >
                                        编辑
                                    </Button>
                                    <button
                                        type="button"
                                        className={styles.shareBtn}
                                        onClick={() => publishProject(p.id)}
                                        disabled={p.isPublic}
                                    >
                                        <Icon name={p.isPublic ? 'check' : 'share'} size={18} />
                                        {p.isPublic ? '已发布' : '发布到广场'}
                                    </button>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            </div>
        </div>
    );
}
